import { z } from "zod";
import { EXPENSE_STATUS, expenseSpentAtSchema } from "./expenses.js";
import { EXPENSE_APPROVE_VOTE_REQUIRED_CODE } from "./expense-decision.js";
import {
  CORRECTION_REASON_MAX,
  businessIdempotencyKeySchema,
  positiveMinorString,
} from "./ledger-correction.js";
import {
  EXPENSE_STATUS_WITH_COOLING,
  expenseStatusWithCoolingSchema,
  type ExpenseStatusWithCooling,
} from "./cooling-freeze-anomaly.js";

/**
 * Shared DTO cho Admin Expenses Oversight (US-AE5.F6-0093 · @UC9164) — liệt kê TOÀN BỘ khoản chi
 * xuyên quỹ/nhóm (mọi status: draft|pending_decision|approved|rejected|posted|reversed), READ-ONLY.
 * Một nguồn sự thật cho api (S03 — list+filter+full-text) và admin web (S04 — render), tránh drift
 * shape ở 2 nơi (DRY). Mirror style admin-consensus.ts NHƯNG list bảng `expenses` TRỰC TIẾP (không
 * qua phiên biểu quyết) ⇒ mọi khoản chi, mọi status, có category/amount/date/full-text filter.
 *
 * BẢO MẬT (invariants — LOAD-BEARING):
 * - REDACTION PII: DTO **LIST** (`adminExpenseItemSchema`) KHÔNG khai `createdBy` (userId người tạo) /
 *   `receiptKeys` (R2 key biên lai) / `description` thô. `.strict()` reject mọi field lạ tại biên.
 *   4 vai admin xem cùng metadata.
 *
 *   🔴 ĐÍNH CHÍNH 24/09/2026 (review PR #939 MEDIUM-2) — câu trên ĐÚNG cho LIST, KHÔNG đúng cho
 *   DETAIL. `adminExpenseDetailSchema` (US-AE11.F6-0848 · @UC9651) **CÓ** `description` thô và mở cho
 *   **cả 4 vai**, vì màn hỗ trợ cần đọc ghi chú để hiểu người dùng đã nhập gì.
 *
 *   ⚠️ ĐÂY LÀ MỘT KHOẢNG TRỐNG CHỜ QĐ CHỦ SẢN PHẨM, ghi ra thay vì để mặc định im lặng:
 *   `description` là **free-text do người dùng nhập**, nên có thể chứa tên trẻ, số điện thoại, số tài
 *   khoản. Nghịch lý cần chủ sản phẩm phán: QĐ-3 giữ kín `payerMemberId` (một uuid, không nói gì về
 *   ai) trong khi cùng response phơi free-text có thể nói rõ hơn nhiều.
 *   Hai đường đi, chưa chọn: (a) redact `description` cho `SUPPORT`/`READ_ONLY` như tiền/PII khác;
 *   (b) giữ nguyên và ghi rõ đây là rủi ro chấp nhận có chủ đích.
 *   🔁 Xem lại khi: có yêu cầu pháp lý về PII, hoặc mở màn này cho vai mới.
 * - B1: `amountMinor` = BigInt minor-units → STRING (regex số nguyên, no float/number JS). NULLABLE
 *   KHÔNG áp (amountMinor NOT NULL ở schema) — nhưng để null-safe join, giữ nullable ở item.
 * - C3: schema `.strict()` — KHÔNG khai báo secret / PII; field lạ bị reject tại biên.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - 🔴 US-AE16.F1-0369 (@UC9630) enum `status` = REUSE `expenseStatusWithCoolingSchema`
 *   (`cooling-freeze-anomaly.ts` — KHÔNG định nghĩa lại, DRY). **8 giá trị**, mirror CHECK db
 *   `expenses_status_check`: draft|pending_decision|approved|rejected|posted|reversed|cooling|cancelled.
 *   TRƯỚC 0369 chỗ này dùng `expenseStatusSchema` (6 giá trị của contract CLIENT) ⇒ mọi khoản chi
 *   `cooling`/`cancelled` làm `map-expense.parse` ném ZodError ⇒ `GET /v1/admin/expenses` **500
 *   VĨNH VIỄN** (sort `createdAt DESC` ⇒ dòng độc ở trang đầu ⇒ chết cả màn). Đây là drift 2
 *   whitelist, cùng họ txType. Mở rộng phải ADDITIVE — status lạ VẪN 400.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size (data lớn → 100). Default (20) do backend áp khi vắng (normalizeLimit). */
export const ADMIN_EXPENSES_MAX_LIMIT = 100;

/**
 * Re-export whitelist status của surface ADMIN (8 giá trị = CHECK db) cho consumer tiện dùng —
 * nguồn gốc `cooling-freeze-anomaly.ts`. `EXPENSE_STATUS` giữ lại vì UI cũ còn tham chiếu hằng tên.
 */
export { EXPENSE_STATUS, EXPENSE_STATUS_WITH_COOLING, expenseStatusWithCoolingSchema };
export type { ExpenseStatusWithCooling };

/** Số nguyên minor-units dạng string (BigInt-safe, KHÔNG float). Dùng cho amount range + item amount. */
const MONEY_INTEGER_STRING = /^\d+$/;

/**
 * `status` filter đa-giá-trị (multi-select). Nhận:
 *  - 1 giá trị đơn: `status=posted`
 *  - CSV: `status=posted,rejected`
 *  - repeated param: `status=posted&status=rejected` (Express gộp thành array)
 * → normalize thành `ExpenseStatusWithCooling[]`. Mỗi phần tử phải ∈ EXPENSE_STATUS_WITH_COOLING
 * (8 giá trị = CHECK db) — reject giá trị lạ. Trước 0369 chỉ nhận 6 ⇒ `?status=cooling` ăn 400.
 */
const statusListSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((v) =>
    (Array.isArray(v) ? v : v.split(",")).map((s) => s.trim()).filter((s) => s.length > 0),
  )
  .pipe(z.array(expenseStatusWithCoolingSchema).min(1))
  .optional();

/** amount range phần tử: string số nguyên minor (BigInt-safe). */
const amountMinorField = z.string().regex(MONEY_INTEGER_STRING, "amount phải là số nguyên minor units").optional();

/**
 * Query list (AC1-AC4 filter server-side + full-text + cursor):
 *  - `fundId?` / `groupId?` (uuid) — lọc theo quỹ / nhóm.
 *  - `status?` — 1|CSV|array ∈ EXPENSE_STATUS_WITH_COOLING (vắng ⇒ TOÀN BỘ trạng thái, AC4).
 *  - `category?` — chuỗi ≤100.
 *  - `from?`/`to?` — ISO datetime, lọc createdAt range (refine from ≤ to).
 *  - `amountMin?`/`amountMax?` — string minor-units (refine min ≤ max).
 *  - `q?` — full-text name+description (≤256), server-side ILIKE contains.
 *  - `cursor?` opaque, `limit?` ∈ [1,100].
 * Validate tại biên (Zod) — single source api ↔ admin.
 */
export const adminExpensesQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    status: statusListSchema,
    category: z.string().trim().min(1).max(100).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    amountMin: amountMinorField,
    amountMax: amountMinorField,
    q: z.string().trim().min(1).max(256).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_EXPENSES_MAX_LIMIT).optional(),
  })
  .strict()
  .refine((v) => !(v.from && v.to) || Date.parse(v.from) <= Date.parse(v.to), {
    message: "from phải ≤ to",
    path: ["from"],
  })
  .refine((v) => !(v.amountMin && v.amountMax) || BigInt(v.amountMin) <= BigInt(v.amountMax), {
    message: "amountMin phải ≤ amountMax",
    path: ["amountMin"],
  });

export type AdminExpensesQuery = z.infer<typeof adminExpensesQuerySchema>;

/**
 * `amountMinor` = BigInt minor-units → string (B1, no float) HOẶC null (null-safe). regex chỉ nhận
 * chuỗi số nguyên (dấu âm cho phép — reversal/điều chỉnh có thể âm) — reject float `"123.45"`.
 */
const amountMinorString = z
  .string()
  .regex(/^-?\d+$/, "amountMinor phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/**
 * List item (AC1). `name`/`category`/`fundName`/`groupName`/`amountMinor` NULLABLE+OPTIONAL
 * (join miss / cột nullable schema). `status` = EXPENSE_STATUS_WITH_COOLING (**8 giá trị** = CHECK
 * db). KHÔNG BAO GIỜ chứa `createdBy` / `receiptKeys` / `description` thô (`.strict()` reject field
 * lạ tại biên — PII).
 *
 * 🔴 US-AE16.F1-0369-S02 (@UC9631 @UC9632) thêm 2 trường ĐỌC-THÊM:
 *  - `postAfter` — mốc worker sẽ TỰ ghi sổ khoản chi `cooling`. Không có nó, admin thấy dòng
 *    "chờ ghi sổ" mà **không biết bao giờ tiền rời quỹ**. KHÔNG phải số tiền ⇒ mọi vai xem được.
 *  - `payerMemberId` — NGƯỜI ỨNG TIỀN (chủ nợ thật của peer-debt sinh từ khoản chi).
 *    🔴 Đọc lại đúng cột đã KHAI TƯỜNG MINH lúc tạo — **TUYỆT ĐỐI KHÔNG suy từ người bấm duyệt**
 *    (CLAUDE.md §3.6, bug đắt nhất repo). NULL ⇒ trả `null`, không đoán.
 *    Là DANH TÍNH ⇒ redact theo vai ở TẦNG SERVER; quy ước lane admin: ẩn = `null`.
 */
export const adminExpenseItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable().optional(),
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    amountMinor: amountMinorString,
    currency: z.string(),
    status: expenseStatusWithCoolingSchema,
    createdAt: z.string(),
    postAfter: z.string().nullable(),
    payerMemberId: z.string().nullable(),
  })
  .strict();

export type AdminExpenseItem = z.infer<typeof adminExpenseItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminExpensesResponseSchema = z.object({
  data: z.array(adminExpenseItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminExpensesResponse = z.infer<typeof adminExpensesResponseSchema>;


// ════════════════════════════════════════════════════════════════════════════════════════════════
// US-AE11.F6-0848 (@UC9651 @UC9652 @UC9653 @UC9654) — ADMIN SỬA KHOẢN CHI HỖ TRỢ NGƯỜI DÙNG.
//
// Bối cảnh: trước task này module admin-expenses có ĐÚNG MỘT route `@Get()` (list) ⇒ người dùng
// nhập sai (chi 500k log thành 5tr) thì admin không gỡ được. Bốn bề mặt mới: chi tiết · sửa chữ ·
// sửa tiền · lịch sử.
//
// 🔴 BA QUYẾT ĐỊNH NGƯỜI DÙNG 23/09/2026 ĐƯỢC KHOÁ NGAY Ở BIÊN NÀY — đọc trước khi "bổ sung cho đủ":
//   QĐ-3: KHÔNG phơi `payerMemberId` / `fundPortionMinor` / suất chia ở màn chi tiết — tính năng đó
//         CHƯA LÀM TỚI. `.strict()` biến "tiện tay thêm sau" thành lỗi 400, chứ không thành một
//         thay đổi lặng lẽ mà không ai đọc lại quyết định.
//   QĐ-1: sửa tiền chỉ cần MỘT người duyệt (khác `UC9104` 4-mắt). Thứ bù lại là `reason` BẮT BUỘC ở
//         CẢ HAI đường ghi + lịch sử đầy đủ từng lần ⇒ `reason` optional là làm hỏng cả bù trừ đó.
//   QĐ-5/6: cổng "không vượt quỹ" và chặn khoản chi có chia suất được ép ở SERVICE (cần đọc DB),
//         biên này chỉ mang MÃ LỖI để client map câu chữ.
// ════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Lý do chỉnh sửa — BẮT BUỘC ở mọi đường ghi của bề mặt này.
 * Trim TRƯỚC rồi mới đo độ dài: `"   "` là chuỗi không rỗng nhưng KHÔNG phải một lý do.
 */
const editReasonSchema = z
  .string()
  .trim()
  .min(1, "Lý do chỉnh sửa là bắt buộc")
  .max(CORRECTION_REASON_MAX);

/**
 * 🔴 ADMIN-EXPENSE-CORRECT — ALLOWLIST status admin được sửa SỐ TIỀN. MỘT nguồn cho server (`correctAmount` ⇒ ngoài danh sách 422
 * `EXPENSE_STATUS_NOT_EDITABLE`, 0 ghi) VÀ client (nút "Sửa số tiền" của admin — FIX-1257 LOW-1: trước đó nút bật cho mọi status rồi để server 422).
 * `draft` · `pending_decision` (QĐ 23/09 — chưa ghi sổ, đổi cột) · `posted` (có bút toán EXPENSE ⇒ đảo + ghi lại; 0 bút toán sống ⇒ đổi cột).
 * Cố ý KHÔNG có `cooling` (QĐ PO 06/10 — coi như đã duyệt; worker post đúng cột tại `postAfter`; sau đó sửa qua lối `posted`) và các status chết
 * `reversed` · `rejected` · `cancelled` · `approved`. ALLOWLIST, không denylist: status mới sau này mặc định bị chặn.
 */
export const ADMIN_AMOUNT_EDITABLE_STATUSES: ReadonlySet<string> = new Set<string>([
  EXPENSE_STATUS.DRAFT,
  EXPENSE_STATUS.PENDING_DECISION,
  EXPENSE_STATUS.POSTED,
]);

/**
 * 🔴 Mã lỗi ỔN ĐỊNH, ngôn-ngữ-trung-tính (§7): backend trả `code`, client map `code→câu chữ`.
 * Backend KHÔNG trả câu đã dịch — `detail` chỉ để debug, không phải nguồn hiển thị.
 *
 * `INSUFFICIENT_FUNDS` **TÁI DÙNG** mã sẵn có của `EarmarkGuardService`, KHÔNG đặt mã mới: hai mã
 * cho cùng một tình huống nghĩa là client phải map hai chỗ, và một trong hai sẽ bị quên.
 */
export const ADMIN_EXPENSE_EDIT_CODES = {
  // 🪦 `EXPENSE_HAS_SPLIT` KHÔNG khai ở đây: tình huống "khoản chi có chia suất" đã có mã ở TẦNG
  // CHUNG `CorrectionService` — `EXPENSE_HAS_SPLIT_CORRECTION_UNSUPPORTED` (422, US-E14.F1-0848).
  // Lane admin ném CHÍNH mã đó. Khai thêm mã riêng là hai mã cho một tình huống (§3.8).
  /** QĐ-5 — vượt phần khả dụng (`Σ CASH − phần đã khoá mục đích`), KHÔNG phải tiền mặt thô. */
  INSUFFICIENT_FUNDS: "INSUFFICIENT_FUNDS",
  /**
   * Mỗi bút toán chỉ đảo được MỘT lần (partial unique) — sửa tiếp phải nhắm bút toán ĐÚNG hiện hành.
   *
   * 🔴 Producer thật (review PR #939 lượt 3, HIGH-1): `correctAmount` ném mã này khi **cột
   * `expenses.amountMinor` LỆCH số tiền của bút toán đang hiệu lực**. Fail-closed: máy không tự đoán
   * bên nào đúng, vì đoán sai là ghi một cặp bút toán dựa trên con số bịa.
   */
  EXPENSE_ALREADY_CORRECTED: "EXPENSE_ALREADY_CORRECTED",
  /**
   * 🔴 FIX-1250 (QĐ điều phối 06/10 — fail-closed trên dữ liệu KHÔNG nhất quán, không phải luật tiền mới). Producer: `correctAmount` khi khoản chi
   * KHÔNG có bút toán `EXPENSE` sống (`ledger/expense-active-tx.ts`) nhưng CÓ bút toán sống KHÁC (≠ REVERSAL, chưa bị đảo) gắn `expenseId`. Đổi
   * cột ở đó là để cột↔sổ lệch (trái §3.3 "sửa = REVERSAL"); đảo bút toán lạ thì đoán sai loại. ⇒ 422, 0 ghi, cần đối soát. Chỉ khi 0 bút toán
   * sống gắn `expenseId` mới đi nhánh "chưa ghi sổ" (đổi cột).
   */
  EXPENSE_LEDGER_INCONSISTENT: "EXPENSE_LEDGER_INCONSISTENT",
  /**
   * 🔴 ADMIN-EXPENSE-CORRECT (QĐ điều phối 06/10 + QĐ PO 06/10 cho `cooling`). Producer: `correctAmount` khi `expenses.status` KHÔNG nằm trong
   * ALLOWLIST `ADMIN_AMOUNT_EDITABLE_STATUSES` = `draft` · `pending_decision` (QĐ 23/09) · `posted`. 422, `params.status`, 0 ghi.
   * Bị chặn: `reversed` · `rejected` · `cancelled` · `approved` (chết — 0 bút toán, không có đường đi tiếp) · `cooling` (QĐ PO 06/10: coi như ĐÃ
   * DUYỆT — worker post đúng CỘT tại `postAfter`, đổi cột ở đây là tiền rời quỹ theo con số không ai duyệt; sau `postAfter` sửa bằng đảo + ghi lại
   * qua lối `posted`). Là ALLOWLIST: status mới thêm sau này MẶC ĐỊNH bị chặn tới khi có người quyết.
   */
  EXPENSE_STATUS_NOT_EDITABLE: "EXPENSE_STATUS_NOT_EDITABLE",
  // 🪦 `EXPENSE_EARMARK_DECREASE_UNSUPPORTED` KHÔNG khai: ca "giảm khoản chi earmarked" nay ĐƯỢC
  // HỖ TRỢ ở tầng chung (`recordReserveRestoreFromCorrection` trả lại hạn mức bằng một hàng `reserve`).
  // Khai một mã chặn cho ca đã chạy được là chặn oan, và mã 0 producer là nói dối trong hợp đồng.
  // 🪦 `EDIT_REASON_REQUIRED` ĐÃ GỠ (review PR #939 lượt 3, MEDIUM-1). Nó chưa bao giờ có producer:
  // thiếu `reason` bị zod chặn ở BIÊN ⇒ 400 của `ZodValidationPipe`, không đi tới service. Giữ một mã
  // mà 0 đường ném là nói dối trong hợp đồng — client viết nhánh map cho một tình huống không tồn tại,
  // rồi lần sau có người tin rằng nhánh đó đã được kiểm. Cần mã riêng cho ca này thì phải có producer
  // thật trước, không phải khai trước rồi hy vọng.
} as const;
export type AdminExpenseEditCode =
  (typeof ADMIN_EXPENSE_EDIT_CODES)[keyof typeof ADMIN_EXPENSE_EDIT_CODES];

/**
 * Chi tiết 1 khoản Chi cho admin (`GET /v1/admin/expenses/:id` — @UC9651).
 *
 * `ledgerTxId` = bút toán **ĐANG HIỆU LỰC**. 🔴 Sau một lượt sửa tiền phải là bút toán ĐÚNG (mới),
 * KHÔNG phải bút toán đã bị đảo — trả cái cũ là dắt người dùng thẳng vào 409 ở lần sửa kế tiếp.
 * `null` = chưa ghi sổ ⇒ chưa có gì để sửa.
 *
 * `hasSplit` để client **khoá sẵn** nút sửa tiền (QĐ-6). Cho bấm rồi mới báo lỗi là bẫy người dùng:
 * họ soạn xong lý do rồi mới biết công cốc.
 */
export const adminExpenseDetailSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable().optional(),
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    description: z.string().nullable(),
    amountMinor: amountMinorString,
    currency: z.string(),
    status: expenseStatusWithCoolingSchema,
    createdAt: z.string(),
    spentAt: z.string().nullable(),
    postAfter: z.string().nullable(),
    ledgerTxId: z.string().nullable(),
    hasSplit: z.boolean(),
    /**
     * US-AE4.F7-1041 (@UC9991) — vì sao nhân viên KHÔNG duyệt hộ được khoản đang chờ: quỹ phải biểu quyết (cùng luật `_postGate` của lõi,
     * cùng mã 403 của route duyệt) ⇒ màn khoá sẵn nút. `null` = không có lý do chặn từ luật duyệt (hoặc khoản không còn chờ — nút ẩn
     * theo `status`). Nhóm đình chỉ / quỹ đóng băng KHÔNG đi qua cờ này: route trả 409 như mọi thao tác làm hộ khác.
     */
    approveBlockedCode: z.enum([EXPENSE_APPROVE_VOTE_REQUIRED_CODE]).nullable(),
    /**
     * Bối cảnh cho màn chi tiết (@UC9651): ai gửi · quỹ đang ra sao · nhóm nào. BẮT BUỘC có mặt — `null` là «không có / bị ẩn theo
     * vai», vắng field là lỗi biên. Người gửi = SUẤT của `createdBy` trong nhóm (không phải người ứng — QĐ-3 vẫn giữ). Tên ẩn theo vai
     * PII, số dư ẩn theo vai TIỀN (ẩn = `null`, KHÔNG `0`).
     */
    requesterMemberId: z.string().nullable(),
    requesterName: z.string().nullable(),
    fundStatus: z.string(),
    fundBalanceMinor: z.string().regex(/^-?\d+$/, "fundBalanceMinor phải là số nguyên minor units (không float)").nullable(),
    groupType: z.string(),
    groupStatus: z.string(),
    groupMemberCount: z.number().int().nonnegative(),
  })
  .strict();
export type AdminExpenseDetail = z.infer<typeof adminExpenseDetailSchema>;

/**
 * Body `PATCH /v1/admin/expenses/:id` — sửa CHỮ, **0 bút toán** (@UC9652).
 *
 * 🔴 `.strict()` ⇒ `amountMinor` bị TỪ CHỐI TẠI BIÊN (400, 0 row chạm DB). Sửa tiền có endpoint
 * riêng; "một đường sửa tiền duy nhất" là bất biến, không phải cách tổ chức code. Mirror luật đã
 * khoá ở `activity-edit-surface.test.ts` cho đường group-side.
 *
 * `undefined` = giữ nguyên · `null` = xoá về "chưa đặt". Hai nghĩa khác nhau, đừng gộp.
 * `refine` cuối: phải có ≥1 field NỘI DUNG — chỉ gửi mỗi `reason` là không có gì để sửa.
 */
export const adminExpensePatchRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    category: z.string().trim().max(100).nullable().optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    // TÁI DÙNG luật ngày của expense (quá khứ không giới hạn, tương lai ⇒ 400, cùng dung sai lệch
    // đồng hồ). Chép sang đây là mở khe hở §3.8 — chính file `expenses.ts` đã ghi cảnh báo đó.
    spentAt: expenseSpentAtSchema.nullable().optional(),
    reason: editReasonSchema,
  })
  .strict()
  .refine(
    (v) =>
      v.name !== undefined ||
      v.category !== undefined ||
      v.description !== undefined ||
      v.spentAt !== undefined,
    "Phải có ít nhất một thông tin cần sửa (ngoài lý do)",
  );
export type AdminExpensePatchRequest = z.infer<typeof adminExpensePatchRequestSchema>;

/**
 * Body `POST /v1/admin/expenses/:id/amount-correction` — sửa SỐ TIỀN (@UC9653).
 *
 * `amountMinor` tái dùng `positiveMinorString` của ledger-corrections (> 0, ≤ trần BIGINT, string).
 * `correctionKey` = khoá nghiệp vụ tất định — thiếu nó thì bấm hai lần thành HAI cặp bút toán.
 */
export const adminExpenseAmountRequestSchema = z
  .object({
    amountMinor: positiveMinorString,
    reason: editReasonSchema,
    correctionKey: businessIdempotencyKeySchema,
  })
  .strict();
export type AdminExpenseAmountRequest = z.infer<typeof adminExpenseAmountRequestSchema>;

/**
 * Body `POST /v1/admin/expenses/:id/approve` — nhân viên Goppy DUYỆT HỘ khoản chi đang chờ (US-AE4.F7-1041 · @UC9991 · QĐ người dùng
 * 02/11/2026). Chỉ `reason` (vào nhật ký nhân viên): số tiền là số đã khai, người bấm là «Đội hỗ trợ Goppy» — client không khai được gì khác.
 */
export const adminExpenseApproveRequestSchema = z
  .object({
    reason: editReasonSchema,
  })
  .strict();
export type AdminExpenseApproveRequest = z.infer<typeof adminExpenseApproveRequestSchema>;

/**
 * Body `POST /v1/admin/expenses/:id/reject` — nhân viên Goppy TỪ CHỐI HỘ khoản chi đang chờ (US-AE4.F7-1045 · @UC9992 · QĐ người dùng
 * 03/11/2026). CÙNG biên với Duyệt hộ (một schema, không bản đúc): chỉ `reason` — lý do này đi Y NHƯ lý do từ chối của app (tin gửi người
 * gửi + màn app đọc lại) và vào nhật ký nhân viên.
 */
export const adminExpenseRejectRequestSchema = adminExpenseApproveRequestSchema;
export type AdminExpenseRejectRequest = z.infer<typeof adminExpenseRejectRequestSchema>;

/** Ai thực hiện lần sửa. Closed-set — mirror CHECK db `expense_edit_logs_actor_kind`. */
export const ADMIN_EXPENSE_EDIT_ACTOR_KINDS = ["system_admin", "group_member"] as const;
export const adminExpenseEditActorKindSchema = z.enum(ADMIN_EXPENSE_EDIT_ACTOR_KINDS);

/**
 * MỘT dòng lịch sử chỉnh sửa (@UC9654). Sửa N lần ⇒ N dòng, không đè nhau.
 *
 * Hai cột tiền **cùng `null`** = lần sửa này không đụng tiền — khác hẳn `0` (0 là một con số THẬT).
 * `actorName`: danh tính ADMIN NỘI BỘ, redact theo `HIGH_READ_ROLES` = {SUPER_ADMIN, OPS}
 * (`name-resolver.service.ts:17`) — KHÔNG phải `PII_FULL_ROLES` = {SUPER_ADMIN, SUPPORT} vốn dành cho
 * PII người dùng cuối. Vai thấp nhận email **ĐÃ MASK** (`op***@…`), KHÔNG phải `null`: mask giữ được
 * câu "ai đã sửa" cho vai chỉ-đọc mà không phơi địa chỉ thật. `null` chỉ khi dòng lịch sử không gắn
 * người nào (đường hệ thống).
 * `changedFields` không rỗng: một dòng lịch sử "không đổi gì" là dòng vô nghĩa.
 */
export const adminExpenseEditLogItemSchema = z
  .object({
    id: z.string(),
    createdAt: z.string(),
    actorName: z.string().nullable(),
    actorKind: adminExpenseEditActorKindSchema,
    reason: z.string(),
    amountBeforeMinor: amountMinorString,
    amountAfterMinor: amountMinorString,
    changedFields: z.array(z.string()).min(1),
    ledgerTxIdBefore: z.string().nullable(),
    ledgerTxIdAfter: z.string().nullable(),
  })
  .strict();
export type AdminExpenseEditLogItem = z.infer<typeof adminExpenseEditLogItemSchema>;

export const adminExpenseEditLogResponseSchema = z
  .object({
    data: z.array(adminExpenseEditLogItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
  })
  .strict();
export type AdminExpenseEditLogResponse = z.infer<typeof adminExpenseEditLogResponseSchema>;

/** Query `GET /v1/admin/expenses/:id/history` — cursor keyset, giống mọi list khác (§7). */
export const adminExpenseHistoryQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_EXPENSES_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminExpenseHistoryQuery = z.infer<typeof adminExpenseHistoryQuerySchema>;
